// Global storefront behavior: mobile menu, search, accordions, product gallery, variants, quantity.
document.documentElement.classList.remove('no-js');

const formatMoney = (cents, format = '${{amount}}') => {
  const value = Number(cents || 0) / 100;
  const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
  let amount;

  if (token === 'amount_no_decimals') {
    amount = Math.round(value).toLocaleString();
  } else if (token === 'amount_with_comma_separator') {
    amount = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else if (token === 'amount_no_decimals_with_comma_separator') {
    amount = Math.round(value).toLocaleString('de-DE');
  } else {
    amount = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
};

const setPurchaseOptionMode = (container, requestedMode) => {
  if (!container) return;

  const sellingPlanRadio = container.querySelector('[data-purchase-option][value="selling_plan"]');
  const oneTimeRadio = container.querySelector('[data-purchase-option][value="one_time"]');
  const requiresSellingPlan = container.dataset.requiresSellingPlan === 'true';
  const canUseSellingPlan = sellingPlanRadio && !sellingPlanRadio.disabled;
  let mode = requestedMode;

  if (requiresSellingPlan) mode = 'selling_plan';
  if (mode === 'selling_plan' && !canUseSellingPlan) mode = oneTimeRadio ? 'one_time' : 'selling_plan';

  container.querySelectorAll('[data-purchase-option]').forEach((radio) => {
    radio.checked = radio.value === mode;
    radio.closest('.purchase-option')?.classList.toggle('is-selected', radio.checked);
  });

  const panel = container.querySelector('[data-selling-plan-panel]');
  const select = container.querySelector('[data-selling-plan-select]');
  const showPanel = mode === 'selling_plan' && canUseSellingPlan;
  if (panel) panel.hidden = !showPanel;
  if (select) select.disabled = !showPanel;
};

const updateSubscriptionPrice = (container) => {
  const select = container?.querySelector('[data-selling-plan-select]');
  const price = container?.querySelector('[data-subscription-price]');
  const option = select?.selectedOptions?.[0];
  if (!price || !option) return;
  const checkoutCharge = Number(option.dataset.checkoutCharge || option.dataset.price || 0);
  const remainingCharge = Number(option.dataset.remainingCharge || 0);
  const dueNow = container.dataset.dueNowLabel || 'due now';
  const dueLater = container.dataset.dueLaterLabel || 'due later';
  price.textContent = remainingCharge > 0
    ? `${formatMoney(checkoutCharge, window.themeMoneyFormat)} ${dueNow}, ${formatMoney(remainingCharge, window.themeMoneyFormat)} ${dueLater}`
    : `${formatMoney(checkoutCharge, window.themeMoneyFormat)} ${dueNow}`;
};

const getSellingPlanMap = (container) => {
  if (!container) return {};
  if (container._sellingPlanMap) return container._sellingPlanMap;
  const script = container.querySelector('[data-selling-plan-data]');
  try {
    container._sellingPlanMap = JSON.parse(script?.textContent || '{}');
  } catch (_) {
    container._sellingPlanMap = {};
  }
  return container._sellingPlanMap;
};

const refreshSellingPlans = (form, variant) => {
  const container = form?.querySelector('[data-purchase-options]');
  if (!container || !variant) return;

  const allocations = getSellingPlanMap(container)[String(variant.id)] || [];
  const select = container.querySelector('[data-selling-plan-select]');
  const sellingPlanRadio = container.querySelector('[data-purchase-option][value="selling_plan"]');
  const oneTimePrice = container.querySelector('[data-one-time-price]');
  const previousPlanId = select?.value;

  if (oneTimePrice) oneTimePrice.textContent = formatMoney(variant.price, window.themeMoneyFormat);
  if (sellingPlanRadio) sellingPlanRadio.disabled = allocations.length === 0;

  if (select) {
    select.replaceChildren(...allocations.map((allocation) => {
      const option = document.createElement('option');
      option.value = allocation.id;
      const dueNow = container.dataset.dueNowLabel || 'due now';
      const dueLater = container.dataset.dueLaterLabel || 'due later';
      option.textContent = allocation.remainingCharge > 0
        ? `${allocation.name} - ${formatMoney(allocation.checkoutCharge, window.themeMoneyFormat)} ${dueNow}, ${formatMoney(allocation.remainingCharge, window.themeMoneyFormat)} ${dueLater}`
        : `${allocation.name} - ${formatMoney(allocation.checkoutCharge || allocation.price, window.themeMoneyFormat)} ${dueNow}`;
      option.dataset.price = allocation.price;
      option.dataset.checkoutCharge = allocation.checkoutCharge || allocation.price;
      option.dataset.remainingCharge = allocation.remainingCharge || 0;
      option.dataset.recurring = String(Boolean(allocation.recurringDeliveries));
      option.dataset.compareAtPrice = allocation.compareAtPrice || '';
      option.dataset.perDeliveryPrice = allocation.perDeliveryPrice || '';
      option.selected = String(allocation.id) === String(previousPlanId);
      return option;
    }));
  }

  const selectedMode = container.querySelector('[data-purchase-option]:checked')?.value || container.dataset.initialMode;
  setPurchaseOptionMode(container, selectedMode);
  updateSubscriptionPrice(container);
};

document.addEventListener('click', async (event) => {
  const toggle = event.target.closest('[data-menu-toggle]');
  if (toggle) {
    const menu = document.querySelector('[data-mobile-menu]');
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    menu?.classList.toggle('is-open', !expanded);
  }

  const searchToggle = event.target.closest('[data-search-toggle]');
  if (searchToggle) {
    const search = document.querySelector('[data-search-form]');
    const expanded = searchToggle.getAttribute('aria-expanded') === 'true';
    searchToggle.setAttribute('aria-expanded', String(!expanded));
    search?.classList.toggle('is-open', !expanded);
    if (!expanded) {
      search?.querySelector('input')?.focus();
    } else {
      searchToggle.focus();
    }
  }

  const thumb = event.target.closest('[data-gallery-thumb]');
  if (thumb) {
    const main = document.querySelector('[data-gallery-main]');
    const src = thumb.getAttribute('data-full-src');
    const alt = thumb.getAttribute('data-alt') || '';
    const mediaType = thumb.getAttribute('data-media-type') || 'image';
    if (main && src) {
      const thumbs = [...(thumb.closest('.product-gallery__thumbs')?.querySelectorAll('[data-gallery-thumb]') || [])];
      const previousThumb = thumbs.find((item) => item.getAttribute('aria-pressed') === 'true');
      const previousNodes = [...main.childNodes].map((node) => node.cloneNode(true));
      const restorePreviousMedia = () => {
        thumb.hidden = true;
        thumbs.forEach((item) => item.setAttribute('aria-pressed', String(item === previousThumb)));
        main.replaceChildren(...previousNodes);
      };
      thumbs.forEach((item) => item.setAttribute('aria-pressed', String(item === thumb)));

      if (mediaType === 'video') {
        const video = document.createElement('video');
        const poster = thumb.getAttribute('data-poster') || '';
        video.src = src;
        video.poster = poster;
        video.controls = true;
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('aria-label', alt);
        video.addEventListener('error', restorePreviousMedia, { once: true });
        main.replaceChildren(video);
      } else {
        const image = document.createElement('img');
        image.alt = alt;
        image.addEventListener('error', restorePreviousMedia, { once: true });
        image.src = src;
        main.replaceChildren(image);
      }
    }
  }

  const planJump = event.target.closest('[data-plan-jump]');
  if (planJump) {
    const targetId = (planJump.getAttribute('href') || '').replace(/^#/, '');
    const target = targetId ? document.getElementById(targetId) : null;
    const purchaseOptions = target?.querySelector('[data-purchase-options]');
    const planSelect = purchaseOptions?.querySelector('[data-selling-plan-select]');
    const sellingPlanRadio = purchaseOptions?.querySelector('[data-purchase-option][value="selling_plan"]');
    const kind = planJump.dataset.planKind;
    const planOption = [...(planSelect?.options || [])].find((option) => {
      if (option.dataset.recurring === 'true') return false;
      const hasRemainingBalance = Number(option.dataset.remainingCharge || 0) > 0;
      return kind === 'deposit' ? hasRemainingBalance : !hasRemainingBalance;
    });

    if (target && purchaseOptions && planSelect && sellingPlanRadio && planOption) {
      event.preventDefault();
      planSelect.value = planOption.value;
      sellingPlanRadio.checked = true;
      setPurchaseOptionMode(purchaseOptions, 'selling_plan');
      updateSubscriptionPrice(purchaseOptions);
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      planSelect.focus({ preventScroll: true });
    }
  }

  const plus = event.target.closest('[data-quantity-plus]');
  const minus = event.target.closest('[data-quantity-minus]');
  if (plus || minus) {
    const wrap = event.target.closest('[data-quantity]');
    const input = wrap?.querySelector('input');
    if (!input) return;
    const value = Math.max(1, parseInt(input.value || '1', 10) + (plus ? 1 : -1));
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  const starterButton = event.target.closest('[data-refill-starter-submit]');
  if (starterButton) {
    const starter = starterButton.closest('[data-refill-starter-form]');
    const status = starter?.querySelector('[data-refill-starter-status]');
    const coreVariantId = document.querySelector('[data-product-form] [data-variant-id]')?.value;
    const refillVariantId = starterButton.dataset.refillVariantId;
    const sellingPlanId = starter?.querySelector('[data-refill-starter-plan]')?.value;

    if (!coreVariantId || !refillVariantId || !sellingPlanId) {
      if (status) status.textContent = window.themeStarterErrorText || 'We could not add the starter order. Please try again.';
      return;
    }

    starterButton.disabled = true;
    if (status) status.textContent = window.themeStarterAddingText || 'Adding starter order...';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: Number(coreVariantId), quantity: 1 },
            { id: Number(refillVariantId), quantity: 1, selling_plan: Number(sellingPlanId) }
          ]
        })
      });
      if (!response.ok) throw new Error('Cart request failed');
      window.location.assign(starterButton.dataset.cartUrl || `${root}cart`);
    } catch (_) {
      starterButton.disabled = false;
      if (status) status.textContent = window.themeStarterErrorText || 'We could not add the starter order. Please try again.';
    }
  }
});

document.addEventListener('error', (event) => {
  const image = event.target;
  if (!(image instanceof HTMLImageElement)) return;
  const thumb = image.closest('[data-gallery-thumb].product-gallery__thumb--external');
  if (thumb) thumb.hidden = true;
}, true);

document.addEventListener('change', (event) => {
  const languageSelect = event.target.closest('[data-language-selector]');
  if (languageSelect) {
    languageSelect.form?.submit();
    return;
  }

  const purchaseOption = event.target.closest('[data-purchase-option]');
  if (purchaseOption) {
    const container = purchaseOption.closest('[data-purchase-options]');
    setPurchaseOptionMode(container, purchaseOption.value);
    updateSubscriptionPrice(container);
    return;
  }

  const sellingPlanSelect = event.target.closest('[data-selling-plan-select]');
  if (sellingPlanSelect) {
    updateSubscriptionPrice(sellingPlanSelect.closest('[data-purchase-options]'));
    return;
  }

  const select = event.target.closest('[data-option-position]');
  if (!select) return;

  const form = select.closest('[data-product-form]');
  const variantsScript = form?.querySelector('[data-product-variants]');
  const idInput = form?.querySelector('[data-variant-id]');
  if (!form || !variantsScript || !idInput) return;

  const selected = Array.from(form.querySelectorAll('[data-option-position]')).map((el) => el.value);
  const variants = JSON.parse(variantsScript.textContent);
  const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
  const submit = form.querySelector('.product-form__submit');
  const productInfo = form.closest('.product-info');
  const price = productInfo?.querySelector('[data-product-price]');
  const comparePrice = productInfo?.querySelector('[data-product-compare-price]');
  const sku = productInfo?.querySelector('[data-product-sku]');
  const availability = form.querySelector('[data-product-availability]');
  const dynamicCheckout = form.querySelector('[data-dynamic-checkout]');

  if (!variant) {
    idInput.value = '';
    if (submit) {
      submit.disabled = true;
      submit.textContent = window.themeUnavailableText || 'Unavailable';
    }
    if (availability) availability.textContent = window.themeUnavailableText || 'Unavailable';
    if (dynamicCheckout) dynamicCheckout.hidden = true;
    refreshSellingPlans(form, null);
    return;
  }

  idInput.value = variant.id;
  if (submit) {
    submit.disabled = !variant.available;
    submit.textContent = variant.available ? window.themeAddToCartText || 'Add to cart' : window.themeSoldOutText || 'Sold out';
  }
  if (price) price.textContent = formatMoney(variant.price, window.themeMoneyFormat);
  if (comparePrice) {
    const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
    comparePrice.hidden = !showCompare;
    comparePrice.textContent = showCompare ? formatMoney(variant.compare_at_price, window.themeMoneyFormat) : '';
  }
  if (sku) sku.textContent = variant.sku || '';
  if (availability) {
    availability.textContent = variant.available
      ? window.themeAvailableText || 'Available'
      : window.themeSoldOutText || 'Sold out';
  }
  if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;
  refreshSellingPlans(form, variant);

  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  window.history.replaceState({}, '', url);
});

document.querySelectorAll('[data-purchase-options]').forEach((container) => {
  const mode = container.querySelector('[data-purchase-option]:checked')?.value || container.dataset.initialMode;
  setPurchaseOptionMode(container, mode);
  updateSubscriptionPrice(container);
});

document.querySelectorAll('[data-product-recommendations]').forEach(async (container) => {
  const url = container.dataset.url;
  if (!url || container.dataset.loaded === 'true') return;

  try {
    const response = await fetch(url);
    if (!response.ok) return;
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const replacement = documentFragment.querySelector('[data-product-recommendations]');
    if (replacement?.innerHTML.trim()) container.replaceWith(replacement);
  } catch (_) {
    // Recommendations are optional; the product page remains usable without them.
  }
});

document.querySelectorAll('[data-drop-countdown]').forEach((countdown) => {
  const deadline = new Date(countdown.dataset.deadline || '');
  if (Number.isNaN(deadline.getTime())) {
    countdown.hidden = true;
    return;
  }

  const fields = {
    days: countdown.querySelector('[data-countdown-days]'),
    hours: countdown.querySelector('[data-countdown-hours]'),
    minutes: countdown.querySelector('[data-countdown-minutes]'),
    seconds: countdown.querySelector('[data-countdown-seconds]'),
  };
  const complete = countdown.querySelector('[data-countdown-complete]');
  const units = countdown.querySelector('.drop-countdown__units');
  let timerId;

  const updateCountdown = () => {
    const remaining = deadline.getTime() - Date.now();
    if (remaining <= 0) {
      if (units) units.hidden = true;
      if (complete) complete.hidden = false;
      window.clearInterval(timerId);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
    Object.entries(values).forEach(([key, value]) => {
      if (fields[key]) fields[key].textContent = String(value).padStart(2, '0');
    });
  };

  updateCountdown();
  timerId = window.setInterval(updateCountdown, 1000);
});

document.querySelectorAll('[data-campaign-progress]').forEach((meter) => {
  const deadline = new Date(meter.dataset.deadline || '');
  const durationDays = Math.max(1, Number(meter.dataset.durationDays || 90));
  if (Number.isNaN(deadline.getTime()) || !Number.isFinite(durationDays)) {
    meter.hidden = true;
    return;
  }

  const durationMs = durationDays * 86400000;
  const start = deadline.getTime() - durationMs;
  const value = meter.querySelector('[data-campaign-progress-value]');
  const valueText = value?.querySelector('[data-campaign-progress-text]');
  const fill = meter.querySelector('[data-campaign-progress-fill]');
  const progressbar = meter.querySelector('[data-campaign-progress-bar]');

  const renderCampaignProgress = () => {
    const elapsed = Date.now() - start;
    const progress = Math.min(100, Math.max(0, (elapsed / durationMs) * 100));
    const formatted = `${progress.toFixed(2)}%`;

    if (value) {
      value.setAttribute('aria-label', `${progress.toFixed(2)} percent`);
      if (valueText) valueText.textContent = formatted;
    }
    if (fill) fill.style.width = `${progress}%`;
    if (progressbar) progressbar.setAttribute('aria-valuenow', progress.toFixed(2));
  };

  renderCampaignProgress();
  window.setInterval(renderCampaignProgress, 1000);
});

const marketingPopup = document.querySelector('[data-marketing-popup]');
if (marketingPopup) {
  const campaignKey = marketingPopup.dataset.campaignKey || 'default';
  const storageKey = `rellumo-email-offer:${campaignKey}`;
  const delay = Math.max(5, Number(marketingPopup.dataset.delaySeconds || 15)) * 1000;
  const frequency = Math.max(1, Number(marketingPopup.dataset.frequencyDays || 30)) * 86400000;
  const success = marketingPopup.querySelector('[data-marketing-success]');

  const readState = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch (_) { return {}; }
  };
  const writeState = (state) => {
    try { localStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) { /* Storage can be unavailable in private modes. */ }
  };
  const showPopup = () => {
    marketingPopup.hidden = false;
    requestAnimationFrame(() => marketingPopup.classList.add('is-visible'));
  };
  const closePopup = () => {
    marketingPopup.classList.remove('is-visible');
    writeState({ ...readState(), dismissedAt: Date.now() });
    window.setTimeout(() => { marketingPopup.hidden = true; }, 220);
  };

  marketingPopup.querySelector('[data-marketing-popup-close]')?.addEventListener('click', closePopup);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !marketingPopup.hidden) closePopup();
  });

  if (success) {
    writeState({ subscribedAt: Date.now() });
    showPopup();
  } else if (window.Shopify?.designMode) {
    showPopup();
  } else {
    const state = readState();
    const dismissedRecently = state.dismissedAt && Date.now() - state.dismissedAt < frequency;
    const alreadySubscribed = Boolean(state.subscribedAt);
    if (!dismissedRecently && !alreadySubscribed) window.setTimeout(showPopup, delay);
  }
}

// Early Access: one shared reservation flow for product cards and product pages.
(() => {
  const dialog = document.querySelector('[data-reservation-dialog]');

  const pendingAnalyticsEvents = [];
  const firstPartyEvents = new Set([
    'campaign_view',
    'campaign_cta_clicked',
    'product_view',
    'reserve_clicked',
    'reservation_started',
    'reservation_completed',
    'priority_offer_viewed',
    'priority_amount_changed',
    'priority_checkout_started',
    'priority_offer_declined',
    'product_shared',
  ]);
  const randomAnalyticsId = (prefix) => {
    if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
  };
  const storedAnalyticsId = (storage, key, prefix) => {
    try {
      const existing = storage.getItem(key);
      if (existing) return existing;
      const created = randomAnalyticsId(prefix);
      storage.setItem(key, created);
      return created;
    } catch (_) {
      return randomAnalyticsId(prefix);
    }
  };
  const sendFirstPartyEvent = (payload) => {
    const endpoint = (dialog?.dataset.reservationEndpoint || '').trim().replace(/\/$/, '');
    const hasProductIdentity = Boolean(payload.product_id && payload.variant_id);
    const hasCampaignIdentity = Boolean(payload.campaign && ['campaign_view', 'campaign_cta_clicked'].includes(payload.event));
    if (!endpoint || !firstPartyEvents.has(payload.event) || (!hasProductIdentity && !hasCampaignIdentity)) return;
    const body = {
      ...payload,
      event_id: randomAnalyticsId('event'),
      visitor_id: storedAnalyticsId(localStorage, 'rellumo:analytics-visitor', 'visitor'),
      session_id: storedAnalyticsId(sessionStorage, 'rellumo:analytics-session', 'session'),
    };
    fetch(`${endpoint}/events`, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});
  };
  let privacyApiUnavailable = false;
  const analyticsPermission = () => {
    if (privacyApiUnavailable) return true;
    const permissionCheck = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed;
    if (typeof permissionCheck !== 'function') return null;
    try { return permissionCheck.call(window.Shopify.customerPrivacy); } catch (_) { return false; }
  };
  const emitAnalyticsEvent = (payload) => {
    if (payload.event === 'campaign_view' && payload.campaign) {
      try { sessionStorage.setItem(`rellumo:campaign-view:${payload.campaign}`, 'true'); } catch (_) { /* Storage is optional. */ }
    }
    document.dispatchEvent(new CustomEvent(`rellumo:${payload.event}`, { detail: payload }));
    window.dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
    window.dataLayer.push(payload);
    if (window.Shopify?.analytics?.publish) {
      try {
        const publication = window.Shopify.analytics.publish(`rellumo:${payload.event}`, payload);
        if (publication?.catch) publication.catch(() => {});
      } catch (_) { /* Pixels remain optional. */ }
    }
    sendFirstPartyEvent(payload);
  };
  const flushAnalyticsEvents = () => {
    const permission = analyticsPermission();
    if (permission === true) pendingAnalyticsEvents.splice(0).forEach(emitAnalyticsEvent);
    else if (permission === false) pendingAnalyticsEvents.length = 0;
  };
  const track = (eventName, detail = {}) => {
    const payload = { ...detail, event: eventName, occurred_at: new Date().toISOString() };
    const permission = analyticsPermission();
    if (permission === true) {
      emitAnalyticsEvent(payload);
      return true;
    }
    if (permission === null && pendingAnalyticsEvents.length < 50) {
      pendingAnalyticsEvents.push(payload);
      return true;
    }
    return false;
  };

  const continueWithoutCustomerPrivacy = () => {
    privacyApiUnavailable = true;
    pendingAnalyticsEvents.splice(0).forEach(emitAnalyticsEvent);
  };

  document.addEventListener('visitorConsentCollected', () => {
    privacyApiUnavailable = false;
    flushAnalyticsEvents();
  });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => {
    privacyApiUnavailable = false;
    flushAnalyticsEvents();
  });

  let privacyFeatureRequested = false;
  const initializeCustomerPrivacy = () => {
    if (window.Shopify?.customerPrivacy) {
      privacyApiUnavailable = false;
      flushAnalyticsEvents();
      return true;
    }
    if (!privacyFeatureRequested && typeof window.Shopify?.loadFeatures === 'function') {
      privacyFeatureRequested = true;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) flushAnalyticsEvents();
        else continueWithoutCustomerPrivacy();
      });
      return true;
    }
    return false;
  };

  if (!initializeCustomerPrivacy()) {
    let attempts = 0;
    const privacyBootstrap = window.setInterval(() => {
      attempts += 1;
      if (initializeCustomerPrivacy()) window.clearInterval(privacyBootstrap);
      else if (attempts >= 20) {
        window.clearInterval(privacyBootstrap);
        continueWithoutCustomerPrivacy();
      }
    }, 250);
  }

  window.RellumoEarlyAccess = { track };

  document.querySelectorAll('[data-launch-momentum]').forEach((section) => {
    const filters = section.querySelectorAll('[data-launch-filter]');
    const items = section.querySelectorAll('[data-launch-item]');
    const empty = section.querySelector('[data-launch-empty]');

    filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        const requested = filter.dataset.launchFilter || 'all';
        let visible = 0;
        filters.forEach((item) => {
          const active = item === filter;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        items.forEach((item) => {
          const show = requested === 'all' || item.dataset.launchItem === requested;
          item.hidden = !show;
          if (show) visible += 1;
        });
        if (empty) empty.hidden = visible > 0;
      });
    });
  });

  const copyShareUrl = async (url) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return;
    }

    const field = document.createElement('textarea');
    field.value = url;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.append(field);
    field.select();
    const copied = document.execCommand('copy');
    field.remove();
    if (!copied) throw new Error('Share link could not be copied');
  };

  document.addEventListener('click', async (event) => {
    const shareButton = event.target.closest('[data-product-share]');
    if (!shareButton) return;

    const shareData = {
      title: shareButton.dataset.shareTitle || document.title,
      text: shareButton.dataset.shareText || '',
      url: shareButton.dataset.shareUrl || window.location.href,
    };
    const label = shareButton.querySelector('[data-share-label]');
    const originalLabel = shareButton.dataset.shareLabel || label?.textContent || '';
    let channel = '';

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        channel = 'native_share';
      } else {
        await copyShareUrl(shareData.url);
        channel = 'clipboard';
        if (label) label.textContent = shareButton.dataset.shareCopied || originalLabel;
        window.setTimeout(() => { if (label) label.textContent = originalLabel; }, 2200);
      }

      track('product_shared', {
        product_id: Number(shareButton.dataset.productId || 0),
        variant_id: Number(shareButton.dataset.variantId || 0),
        product_handle: shareButton.dataset.productHandle || '',
        channel,
      });
    } catch (error) {
      if (error?.name !== 'AbortError' && label) label.textContent = originalLabel;
    }
  });

  if (!dialog) return;

  const form = dialog.querySelector('#RellumoReservationForm');
  const formStep = dialog.querySelector('[data-reservation-step="form"]');
  const priorityStep = dialog.querySelector('[data-reservation-step="priority"]');
  const completeStep = dialog.querySelector('[data-reservation-step="complete"]');
  const variantSelect = dialog.querySelector('[data-reservation-variant]');
  const quantitySelect = dialog.querySelector('[data-reservation-quantity]');
  const errorMessage = dialog.querySelector('[data-reservation-error]');
  const priorityErrorMessage = dialog.querySelector('[data-priority-error]');
  const submitButton = dialog.querySelector('[data-reservation-submit]');
  const prioritySubmit = dialog.querySelector('[data-priority-submit]');
  const priorityRange = dialog.querySelector('[data-priority-range]');
  const priorityPresets = dialog.querySelector('[data-priority-presets]');
  const priorityUnavailable = dialog.querySelector('[data-priority-unavailable]');
  const priorityToday = dialog.querySelector('[data-priority-today]');
  const priorityRemaining = dialog.querySelector('[data-priority-remaining]');
  const paidFullMessage = dialog.querySelector('[data-priority-paid-full]');
  const reservationManageLink = dialog.querySelector('[data-reservation-manage]');
  const reservationVerification = dialog.querySelector('[data-reservation-verification]');
  const completeVerification = dialog.querySelector('[data-reservation-complete-verification]');
  const phoneInput = dialog.querySelector('[data-reservation-phone]');
  let activeProduct = null;
  let activeVariant = null;
  let activePlans = [];
  let activePlan = null;
  let approvedPriorityAccess = null;
  let reservationReference = '';
  let lastReservationTrigger = null;
  let fallbackDialogOpen = false;

  const setStep = (step) => {
    formStep.hidden = step !== 'form';
    priorityStep.hidden = step !== 'priority';
    completeStep.hidden = step !== 'complete';
    dialog.scrollTop = 0;
    const activeStep = step === 'priority' ? priorityStep : step === 'complete' ? completeStep : null;
    if (activeStep) window.requestAnimationFrame(() => activeStep.querySelector('[data-reservation-step-heading]')?.focus());
  };

  const readConfig = (id) => {
    try { return JSON.parse(document.getElementById(id)?.textContent || ''); } catch (_) { return null; }
  };

  const storeReservation = (record) => {
    try {
      const key = 'rellumo:early-access-reservations';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const next = [...existing.filter((item) => item.reference !== record.reference), record].slice(-30);
      localStorage.setItem(key, JSON.stringify(next));
    } catch (_) {
      // The reservation is still submitted when browser storage is unavailable.
    }
  };

  const makeReference = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `rlm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const money = (cents) => formatMoney(cents, window.themeMoneyFormat || '${{amount}}');
  const selectedQuantity = () => Math.max(1, Number(quantitySelect.value) || 1);
  const reservationEndpoint = () => (dialog.dataset.reservationEndpoint || '').trim();
  const reservationServiceAvailable = () => reservationEndpoint().length > 0;
  const priorityAccessEnabled = () => dialog.dataset.priorityEnabled === 'true';
  const priorityPolicyReady = () => dialog.dataset.priorityPolicyReady === 'true';
  const setManagementUrl = (value) => {
    reservationManageLink.hidden = true;
    reservationManageLink.removeAttribute('href');
    if (!value) return;

    try {
      const url = new URL(String(value), window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/apps/rellumo-reservations/manage/')) return;
      reservationManageLink.href = url.href;
      reservationManageLink.hidden = false;
    } catch (_) {
      // Invalid management links remain hidden.
    }
  };

  const updatePriorityDisplay = (index, shouldTrack = false) => {
    if (!activePlans.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), activePlans.length - 1);
    activePlan = activePlans[safeIndex];
    const quantity = selectedQuantity();
    const amountCents = Number(activePlan.checkoutChargeCents) * quantity;
    const remainingCents = Number(activePlan.remainingCents) * quantity;
    const paidInFull = activePlan.remainingCents <= 0;

    priorityRange.value = String(safeIndex);
    priorityToday.textContent = money(amountCents);
    priorityRemaining.textContent = paidInFull ? money(0) : money(remainingCents);
    paidFullMessage.hidden = !paidInFull;
    const todayLabel = priorityToday.previousElementSibling?.textContent?.trim() || 'Today';
    const remainingLabel = priorityRemaining.previousElementSibling?.textContent?.trim() || 'Remaining';
    priorityRange.setAttribute(
      'aria-valuetext',
      `${todayLabel}: ${money(amountCents)}; ${remainingLabel}: ${money(paidInFull ? 0 : remainingCents)}`,
    );
    prioritySubmit.textContent = paidInFull
      ? prioritySubmit.dataset.labelPaidFull
      : `${prioritySubmit.dataset.labelSecure} — ${money(amountCents)}`;
    priorityPresets.querySelectorAll('button').forEach((button, buttonIndex) => {
      const selected = buttonIndex === safeIndex;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    if (shouldTrack && !priorityStep.hidden) {
      track('priority_amount_changed', {
        product_id: activeProduct.productId,
        variant_id: activeVariant.id,
        quantity,
        amount_cents: amountCents,
        remaining_cents: remainingCents,
        selling_plan_id: activePlan.id,
      });
    }
  };

  const configurePriority = () => {
    const contract = approvedPriorityAccess;
    const minimum = Number(contract?.minimumPaymentCents);
    const approvedOptions = new Map((Array.isArray(contract?.options) ? contract.options : [])
      .filter((option) => /^\d{1,20}$/.test(String(option?.sellingPlanId || ''))
        && Number.isSafeInteger(Number(option?.initialPaymentCents)))
      .map((option) => [String(option.sellingPlanId), Number(option.initialPaymentCents)]));
    const contractMatchesVariant = priorityAccessEnabled()
      && reservationServiceAvailable()
      && priorityPolicyReady()
      && contract?.currency === activeProduct?.currency
      && Number(contract?.retailPriceCents) === Number(activeVariant?.priceCents)
      && Number(activeVariant?.minimumPriorityCents) === minimum;
    activePlans = (contractMatchesVariant ? [...(activeVariant?.sellingPlans || [])] : [])
      .filter((plan) => {
        const checkout = Number(plan.checkoutChargeCents);
        const remaining = Number(plan.remainingCents);
        const total = Number(plan.totalPriceCents ?? checkout + remaining);
        const variantPrice = Number(activeVariant?.priceCents || 0);
        return approvedOptions.get(String(plan.id)) === checkout
          && checkout >= minimum
          && checkout <= total
          && remaining >= 0
          && checkout + remaining === total
          && total === variantPrice;
      })
      .sort((left, right) => left.checkoutChargeCents - right.checkoutChargeCents);
    activePlan = activePlans[0] || null;
    priorityPresets.replaceChildren();
    priorityUnavailable.hidden = activePlans.length > 0;
    prioritySubmit.disabled = activePlans.length === 0;
    priorityRange.hidden = activePlans.length < 2;
    priorityRange.disabled = activePlans.length < 2;

    if (!activePlans.length) {
      priorityToday.textContent = '—';
      priorityRemaining.textContent = '—';
      priorityRange.setAttribute('aria-valuetext', '');
      paidFullMessage.hidden = true;
      return;
    }

    priorityRange.min = '0';
    priorityRange.max = String(activePlans.length - 1);
    priorityRange.value = '0';
    activePlans.forEach((plan, index) => {
      const preset = document.createElement('button');
      const paidInFull = plan.remainingCents <= 0;
      const amountCents = Number(plan.checkoutChargeCents) * selectedQuantity();
      preset.type = 'button';
      preset.textContent = paidInFull ? prioritySubmit.dataset.labelPaidFull : money(amountCents);
      preset.setAttribute('aria-pressed', String(index === 0));
      preset.classList.toggle('is-selected', index === 0);
      preset.addEventListener('click', () => updatePriorityDisplay(index, true));
      priorityPresets.append(preset);
    });
    updatePriorityDisplay(0);
  };

  const updateVariant = () => {
    activeVariant = activeProduct?.variants?.find((variant) => String(variant.id) === String(variantSelect.value))
      || activeProduct?.variants?.[0]
      || null;
    if (!activeVariant) return;
    dialog.querySelector('[data-reservation-product-price]').textContent = activeVariant.priceFormatted;
    configurePriority();
  };

  const openDialog = (config, trigger) => {
    activeProduct = config;
    lastReservationTrigger = trigger || document.activeElement;
    reservationReference = makeReference();
    approvedPriorityAccess = null;
    form.reset();
    submitButton.disabled = false;
    form.querySelector('[data-reservation-reference-field]').value = reservationReference;
    form.querySelector('[data-reservation-product-field]').value = config.title;
    form.querySelector('[data-reservation-handle-field]').value = config.handle;
    dialog.querySelector('[data-reservation-product-title]').textContent = config.title;
    errorMessage.hidden = true;
    priorityErrorMessage.hidden = true;
    if (reservationVerification) reservationVerification.hidden = true;
    if (completeVerification) completeVerification.hidden = true;
    setManagementUrl(null);
    setStep('form');

    const imageWrap = dialog.querySelector('[data-reservation-image-wrap]');
    const image = dialog.querySelector('[data-reservation-image]');
    imageWrap.hidden = !config.image;
    if (config.image) {
      image.src = config.image;
      image.alt = config.title;
    }

    const reservableVariants = config.variants.filter((variant) => (
      variant.available === true || variant.reservationEligible === true
    ));
    if (!reservableVariants.length) {
      const unavailableOption = document.createElement('option');
      unavailableOption.value = '';
      unavailableOption.textContent = dialog.dataset.reservationUnavailable || 'Reservations are not available for this product yet.';
      variantSelect.replaceChildren(unavailableOption);
      variantSelect.disabled = true;
      submitButton.disabled = true;
      errorMessage.textContent = dialog.dataset.reservationUnavailable || 'Reservations are not available for this product yet.';
      errorMessage.hidden = false;
    } else {
      variantSelect.disabled = false;
      variantSelect.replaceChildren(...reservableVariants.map((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title === 'Default Title' ? config.title : variant.title;
        option.selected = String(variant.id) === String(config.selectedVariantId);
        return option;
      }));
      updateVariant();
    }

    if (typeof dialog.showModal === 'function') {
      fallbackDialogOpen = false;
      dialog.showModal();
    } else {
      fallbackDialogOpen = true;
      dialog.setAttribute('open', '');
      dialog.setAttribute('role', 'dialog');
    }
    document.body.classList.add('has-open-dialog');
    dialog.querySelector('input:not([type="hidden"])')?.focus();
    track('reserve_clicked', { product_id: config.productId, variant_id: activeVariant?.id, product_handle: config.handle });
    track('reservation_started', { product_id: config.productId, variant_id: activeVariant?.id, product_handle: config.handle });
  };

  const closeDialog = () => {
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
      return;
    }
    fallbackDialogOpen = false;
    dialog.removeAttribute('open');
    document.body.classList.remove('has-open-dialog');
    window.requestAnimationFrame(() => lastReservationTrigger?.focus());
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-reserve-trigger]');
    if (trigger) {
      const config = readConfig(trigger.dataset.reservationConfigId);
      if (config) openDialog(config, trigger);
    }

    if (event.target.closest('[data-reservation-close]')) closeDialog();

    if (event.target.closest('[data-priority-decline]')) {
      track('priority_offer_declined', {
        product_id: activeProduct?.productId,
        variant_id: activeVariant?.id,
        reservation_reference: reservationReference,
      });
      setStep('complete');
    }
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog.addEventListener('close', () => {
    fallbackDialogOpen = false;
    document.body.classList.remove('has-open-dialog');
    window.requestAnimationFrame(() => lastReservationTrigger?.focus());
  });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }
    if (!fallbackDialogOpen) return;
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  variantSelect.addEventListener('change', updateVariant);
  quantitySelect.addEventListener('change', configurePriority);
  priorityRange.addEventListener('input', () => updatePriorityDisplay(priorityRange.value));
  priorityRange.addEventListener('change', () => updatePriorityDisplay(priorityRange.value, true));

  const callingCodes = Object.freeze({
    US: '1', CA: '1', GB: '44', DE: '49', FR: '33', ES: '34', IT: '39',
    AU: '61', NZ: '64', IE: '353', NL: '31', BE: '32', CH: '41', AT: '43',
  });
  const normalizePhone = () => {
    if (!phoneInput) return true;
    phoneInput.setCustomValidity('');
    const raw = phoneInput.value.trim();
    if (!raw) return true;
    const digits = raw.replace(/\D/g, '');
    let normalized = '';
    if (raw.startsWith('+')) normalized = `+${digits}`;
    else if (raw.startsWith('00')) normalized = `+${digits.slice(2)}`;
    else {
      const country = String(dialog.dataset.phoneCountry || '').toUpperCase();
      const callingCode = callingCodes[country];
      if (callingCode) {
      const national = digits.startsWith(callingCode) && digits.length >= 8
          ? digits
          : `${callingCode}${digits.replace(/^0+/, '')}`;
        normalized = `+${national}`;
      }
    }
    if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
      phoneInput.setCustomValidity(dialog.dataset.phoneInvalid || 'Include a valid country code.');
      phoneInput.reportValidity();
      return false;
    }
    phoneInput.value = normalized;
    return true;
  };
  phoneInput?.addEventListener('input', () => phoneInput.setCustomValidity(''));

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!normalizePhone() || !form.reportValidity() || submitButton.disabled) return;

    submitButton.disabled = true;
    errorMessage.hidden = true;
    const formData = new FormData(form);
    const body = [
      `Reservation reference: ${reservationReference}`,
      `Product: ${activeProduct.title}`,
      `Handle: ${activeProduct.handle}`,
      `Variant: ${variantSelect.selectedOptions[0]?.textContent || ''}`,
      `Variant ID: ${activeVariant.id}`,
      `Quantity: ${quantitySelect.value}`,
      'State: RESERVED',
    ].join('\n');
    form.querySelector('[data-reservation-body-field]').value = body;
    formData.set('contact[body]', body);

    const record = {
      reference: reservationReference,
      state: 'RESERVED',
      productId: activeProduct.productId,
      productHandle: activeProduct.handle,
      variantId: activeVariant.id,
      quantity: Number(quantitySelect.value || 1),
      reservedAt: new Date().toISOString(),
    };

    try {
      const endpoint = reservationEndpoint();
      let response;
      if (endpoint) {
        response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Idempotency-Key': reservationReference },
          body: JSON.stringify({
            reference: record.reference,
            state: record.state,
            productId: record.productId,
            productHandle: record.productHandle,
            variantId: record.variantId,
            quantity: record.quantity,
            firstName: formData.get('contact[first_name]'),
            email: formData.get('contact[email]'),
            phone: formData.get('contact[phone]'),
            acceptsMarketing: formData.get('contact[accepts_marketing]') === 'true',
          }),
        });
      } else {
        response = await fetch(form.action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
      }
      if (!response.ok) throw new Error('Reservation request failed');
      if (endpoint) {
        const result = await response.json();
        if (typeof result?.reservationId !== 'string' || !result.reservationId.trim() || result.state !== 'RESERVED') {
          throw new Error('Reservation service returned an invalid record');
        }
        reservationReference = result.reservationId.trim();
        approvedPriorityAccess = result.priorityAccess || null;
        record.reference = reservationReference;
        form.querySelector('[data-reservation-reference-field]').value = reservationReference;
        setManagementUrl(result.manageUrl);
        if (reservationVerification) reservationVerification.hidden = false;
      } else {
        const completedUrl = new URL(response.url || window.location.href, window.location.href);
        if (completedUrl.searchParams.get('contact_posted') !== 'true') {
          throw new Error('Contact reservation was not confirmed');
        }
        setManagementUrl(null);
        approvedPriorityAccess = null;
      }

      storeReservation(record);
      track('reservation_completed', {
        product_id: activeProduct.productId,
        product_handle: activeProduct.handle,
        variant_id: activeVariant.id,
        quantity: record.quantity,
        reservation_reference: reservationReference,
      });
      configurePriority();
      if (activePlans.length > 0) {
        setStep('priority');
        track('priority_offer_viewed', {
          product_id: activeProduct.productId,
          variant_id: activeVariant.id,
          reservation_reference: reservationReference,
          priority_available: true,
        });
      } else {
        if (completeVerification) completeVerification.hidden = !endpoint;
        setStep('complete');
      }
    } catch (_) {
      errorMessage.textContent = dialog.dataset.reservationError || 'We could not save your reservation. Please try again.';
      errorMessage.hidden = false;
    } finally {
      submitButton.disabled = false;
    }
  });

  prioritySubmit.addEventListener('click', async () => {
    if (!reservationServiceAvailable() || !activePlan || !activeVariant || prioritySubmit.disabled) return;
    prioritySubmit.disabled = true;
    priorityErrorMessage.hidden = true;
    const originalLabel = prioritySubmit.textContent;
    prioritySubmit.textContent = prioritySubmit.dataset.labelProcessing;
    const state = activePlan.remainingCents <= 0 ? 'PAID_IN_FULL' : 'PRIORITY';
    const quantity = selectedQuantity();
    const amountCents = Number(activePlan.checkoutChargeCents) * quantity;
    const remainingCents = Number(activePlan.remainingCents) * quantity;

    track('priority_checkout_started', {
      product_id: activeProduct.productId,
      variant_id: activeVariant.id,
      selling_plan_id: activePlan.id,
      quantity,
      amount_cents: amountCents,
      remaining_cents: remainingCents,
      reservation_reference: reservationReference,
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: String(activeVariant.id),
            quantity,
            selling_plan: String(activePlan.id),
            properties: {
              '_Rellumo reservation': reservationReference,
              '_Early Access state': state,
            },
          }],
        }),
      });
      if (!response.ok) throw new Error('Priority checkout could not start');
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch (_) {
      priorityErrorMessage.textContent = dialog.dataset.priorityError || 'Priority checkout could not start. Your free reservation is still secure.';
      priorityErrorMessage.hidden = false;
      prioritySubmit.disabled = false;
      prioritySubmit.textContent = originalLabel;
    }
  });

  const campaign = document.querySelector('[data-drop-campaign][data-campaign-key]');
  if (campaign) {
    const campaignKey = campaign.dataset.campaignKey;
    const sessionKey = `rellumo:campaign-view:${campaignKey}`;
    let campaignViewedInMemory = false;
    document.addEventListener('rellumo:campaign_view', (event) => {
      if (event.detail?.campaign === campaignKey) campaignViewedInMemory = true;
    });
    const recordCampaignView = () => {
      let campaignViewed = false;
      try { campaignViewed = sessionStorage.getItem(sessionKey) === 'true'; } catch (_) { /* Storage is optional. */ }
      if (!campaignViewedInMemory && !campaignViewed) track('campaign_view', { campaign: campaignKey });
    };
    recordCampaignView();
    document.addEventListener('visitorConsentCollected', recordCampaignView);

    campaign.addEventListener('click', (event) => {
      const action = event.target.closest('[data-campaign-action]')?.dataset.campaignAction;
      if (action) track('campaign_cta_clicked', { campaign: campaignKey, action });
    });
  }

  if (document.querySelector('.product-early-access')) {
    const productConfig = document.querySelector('.product-early-access + [data-early-access-product-config]');
    const parsed = productConfig ? readConfig(productConfig.id) : null;
    if (parsed) track('product_view', {
      product_id: parsed.productId,
      variant_id: parsed.selectedVariantId || parsed.variants?.[0]?.id,
      product_handle: parsed.handle,
    });
  }
})();
